import React, { useEffect, useState } from "react";
import PatientService from "../../../Services/PatientService";
import dayjs from "dayjs";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  BarChart,
} from "recharts";
import { Modal, Button, Spin, message, Typography } from "antd";
import Sleep from "../../../Assets/Images/Svg/Sleep.svg";
import { IoAlarmOutline } from "react-icons/io5";
import { LuBedDouble } from "react-icons/lu";
import { PiTimer } from "react-icons/pi";

const SleepData = ({ smartWatchId }) => {
  const [sleep, setSleep] = useState([]);
  const [sleepDetails, setSleepDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (smartWatchId) fetchSleepData();
  }, [smartWatchId]);

  const fetchSleepData = async (fromDate, toDate) => {
    const today = dayjs();
    const start = fromDate
      ? dayjs(fromDate).format("DD/MM/YYYY")
      : today.format("DD/MM/YYYY");
    const end = toDate ? dayjs(toDate).format("DD/MM/YYYY") : start;

    try {
      setLoading(true);
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 7,
        offset: 0,
        fromDate: start,
        toDate: end,
        type: "SLEEPS",
      });

      if (res?.data.length > 0) {
        const parsedData = res.data.map((item) => {
          let raw = {};
          try {
            raw = JSON.parse(item.RAW_JSON || "{}");
          } catch {}
          return { ...item, RAW_JSON: raw };
        });
        setSleep(parsedData);
        setSleepDetails(parsedData[0]);
      } else {
        setSleep([]);
        setSleepDetails({});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return "0 hr 0 min";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hrs} hr${hrs !== 1 ? "s" : ""} ${mins} min${
      mins !== 1 ? "s" : ""
    }`;
  };

  const showGraph = () => {
    if (!smartWatchId) return;
    setIsModalOpen(true);
  };
  const handleClose = (e) => {
    e.stopPropagation();
    setIsModalOpen(false);
  };

  return (
    <div>
      <div
        title="Click to view Graph"
        className="bg-white shadow rounded-lg p-4 w-full h-40 cursor-pointer"
        onClick={showGraph}
      >
        <div className="flex items-center gap-2 self-start mb-4">
          <img src={Sleep} alt="icon" />
          <h2 className="text-sm font-sans font-semibold">Sleep</h2>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-24">
            <Spin />
          </div>
        ) : (
          <div className="mt-4 text-[11px] grid grid-cols-2 gap-y-3">
            <div>
              <p className="flex items-center gap-1 font-sans font-semibold text-[11px]">
                <IoAlarmOutline className="text-[12px]" />
                {formatTime(sleepDetails?.DURATION_SEC)}
              </p>
              <p className="text-gray-500 text-[10px] mt-0.5">Duration</p>
            </div>

            <div>
              <p className="flex items-center gap-1 font-sans font-semibold text-[11px]">
                <LuBedDouble className="text-[12px]" />
                {formatTime(sleepDetails?.DEEP_SEC)}
              </p>
              <p className="text-gray-500 text-[10px] mt-0.5">Deep Sleep</p>
            </div>

            <div>
              <p className="flex items-center gap-1 font-sans font-semibold text-[11px]">
                <PiTimer className="text-[12px]" />
                {formatTime(sleepDetails?.LIGHT_SEC)}
              </p>
              <p className="text-gray-500 text-[10px] mt-0.5">Light Sleep</p>
            </div>
          </div>
        )}
      </div>

      {smartWatchId && (
        <SleepModal
          open={isModalOpen}
          onClose={handleClose}
          smartWatchId={smartWatchId}
          sleepDetails={sleepDetails}
        />
      )}
    </div>
  );
};

const SleepModal = ({ open, onClose, smartWatchId, sleepDetails }) => {
  const [tab, setTab] = useState("1D");
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({
    total: "0 hr 0 min",
    deep: "0 hr 0 min",
    light: "0 hr 0 min",
    rem: "0 hr 0 min",
    awake: "0 hr 0 min",
  });
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [startDate, setStartDate] = useState(dayjs().subtract(6, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const { Text } = Typography;
  const sleepStages = ["Deep", "Light", "REM", "Awake"];

  const fetch1DSleepData = async () => {
    if (!smartWatchId) return;

    try {
      const date = selectedDate.format("DD/MM/YYYY");
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 7,
        offset: 0,
        fromDate: date,
        toDate: date,
        type: "SLEEPS",
      });

      if (res?.data?.length > 0) {
        const item = res.data[0];
        let raw = {};
        try {
          raw = JSON.parse(item.RAW_JSON || "{}");
        } catch {}

        const segments = [];
        Object.entries(raw.sleepLevelsMap || {}).forEach(([key, value]) => {
          const stageName = key.charAt(0).toUpperCase() + key.slice(1);
          value.forEach((seg) => {
            segments.push({
              stage: stageName,
              start: seg.startTimeInSeconds,
              end: seg.endTimeInSeconds,
            });
          });
        });

        segments.sort((a, b) => a.start - b.start);

        const chartPoints = segments.map((seg) => ({
          time: dayjs.unix(seg.start).format("HH:mm"),
          stage: seg.stage,
          duration: (seg.end - seg.start) / 60,
        }));

        setChartData(chartPoints);

        const sleepSummary = item.sleepSummary
          ? JSON.parse(item.sleepSummary)
          : {};
        setSummary({
          total: sleepSummary?.TotalSleep || "0 hr 0 min",
          deep: sleepSummary?.DeepSleep || "0 hr 0 min",
          light: sleepSummary?.LightSleep || "0 hr 0 min",
          rem: sleepSummary?.REMSleep || "0 hr 0 min",
          awake: sleepSummary?.Awake || "0 hr 0 min",
        });
      } else {
        setChartData([]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetch7DSleepData = async () => {
    if (!smartWatchId) return;
    try {
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 100,
        offset: 0,
        fromDate: startDate.format("DD/MM/YYYY"),
        toDate: endDate.format("DD/MM/YYYY"),
        type: "SLEEPS",
      });

      if (res?.data?.length > 0) {
        const parsedData = res.data
          .map((item) => {
            let raw = {};
            try {
              raw = JSON.parse(item.RAW_JSON || "{}");
            } catch {}
            const sleepDate = item.SLEEP_DATE
              ? dayjs(item.SLEEP_DATE)
              : dayjs(item.CREATED_AT || item.UPDATED_AT || new Date());
            return { ...item, parsedDate: sleepDate, RAW_JSON: raw };
          })
          .sort((a, b) => a.parsedDate.valueOf() - b.parsedDate.valueOf());

        const lineData = parsedData.map((d) => ({
          date: d.parsedDate.format("DD MMM"),
          avg: d.RAW_JSON?.overallSleepScore?.value || 0,
        }));

        setChartData(lineData);
      } else {
        setChartData([]);
      }
    } catch (err) {
      console.error(err);
      setChartData([]);
    }
  };

  useEffect(() => {
    if (tab === "1D") {
      fetch1DSleepData();
    } else {
      fetch7DSleepData();
    }
  }, [tab, smartWatchId, selectedDate, startDate, endDate]);

  const handlePrev1Day = () =>
    setSelectedDate((prev) => prev.subtract(1, "day"));
  const handleNext1Day = () => {
    setSelectedDate((prev) => {
      const next = prev.add(1, "day");
      if (next.isAfter(dayjs(), "day")) {
        message.warning("You can’t view future data");
        return prev;
      }
      return next;
    });
  };

  const handlePrev7D = () => {
    setStartDate((prev) => prev.subtract(7, "day"));
    setEndDate((prev) => prev.subtract(7, "day"));
  };
  const handleNext7D = () => {
    const yesterday = dayjs().subtract(1, "day");
    let newStart = startDate.add(7, "day");
    let newEnd = endDate.add(7, "day");
    if (newEnd.isAfter(yesterday, "day")) {
      newEnd = yesterday;
      newStart = yesterday.subtract(6, "day");
    }
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      title="Sleep Graph"
    >
      <div className="flex flex-col items-center">
        <div className="flex justify-center mb-4 gap-3">
          <button
            onClick={() => setTab("1D")}
            className={`${
              tab === "1D" ? "bg-brand-primaryColor text-white" : "bg-white"
            } px-4 py-2 rounded-md`}
          >
            1D
          </button>
          <button
            className={`${
              tab === "1D" ? "bg-white" : "bg-brand-primaryColor text-white"
            } px-4 py-2 rounded-md`}
            onClick={() => setTab("7D")}
          >
            7D
          </button>
        </div>

        {tab === "7D" && (
          <div className="flex justify-between w-full mb-4">
            <button
              className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
              onClick={handlePrev7D}
            >
              ← Prev 7 Days
            </button>
            <span>
              {startDate.format("DD/MM/YYYY")} - {endDate.format("DD/MM/YYYY")}
            </span>
            <button
              className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
              onClick={handleNext7D}
            >
              Next 7 Days →
            </button>
          </div>
        )}

        {tab === "1D" ? (
          <>
            <div className="flex items-center justify-center gap-6 mb-4">
              <button
                className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
                onClick={handlePrev1Day}
              >
                ←
              </button>
              <strong>{selectedDate.format("DD MMM YYYY")}</strong>
              <button
                className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
                onClick={handleNext1Day}
              >
                →
              </button>
            </div>

            {chartData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={chartData.map((d) => ({
                      ...d,
                      stageIndex:
                        d.stage === "Deep"
                          ? 0
                          : d.stage === "Light"
                          ? 1
                          : d.stage === "REM"
                          ? 2
                          : 3,
                    }))}
                    margin={{ top: 20, right: 20, left: 60, bottom: 20 }}
                  >
                    {/* X-axis = time */}
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 10 }}
                      label={{
                        value: "Time",
                        position: "insideBottom",
                        offset: -5,
                      }}
                    />

                    <YAxis
                      type="number"
                      domain={[0, 3]}
                      ticks={[0, 1, 2, 3]}
                      tickFormatter={(v) =>
                        ["Deep", "Light", "REM", "Awake"][v] || ""
                      }
                      tick={{ fontSize: 12, fontWeight: 600 }}
                      width={70}
                    />

                    <Tooltip
                      formatter={(val, name, props) => [
                        `${props.payload.stage}`,
                        "Stage",
                      ]}
                      labelFormatter={(val) => `Time: ${val}`}
                    />

                    <Bar
                      dataKey="stageIndex"
                      barSize={15}
                      shape={(props) => {
                        const { x, y, width, payload } = props;
                        const colorMap = {
                          Deep: "#1976d2",
                          Light: "#4caf50",
                          REM: "#fbc02d",
                          Awake: "#f44336",
                        };

                        const rowHeight = 45; // height per stage
                        const stageIndex = payload.stageIndex;

                        // y position for the stage row
                        const yPos = 200 - (stageIndex + 1) * rowHeight;
                        const height = rowHeight; // fill the row

                        return (
                          <rect
                            x={x} // start from segment's x
                            y={yPos}
                            width={width} // width of this segment
                            height={height} // fill stage row
                            fill={colorMap[payload.stage] || "#ccc"}
                            rx={3}
                            ry={3}
                          />
                        );
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>

                <div className="mt-4 text-center text-sm">
                  <span className="font-semibold">Total: {summary.total}</span>
                  <span className="ml-3 font-semibold">
                    Deep: {summary.deep}
                  </span>
                  <span className="ml-3 font-semibold">
                    Light: {summary.light}
                  </span>
                  <span className="ml-3 font-semibold">REM: {summary.rem}</span>
                  <span className="ml-3 font-semibold">
                    Awake: {summary.awake}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-gray-500 mt-3">No sleep data available</p>
            )}
          </>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="avg"
                stroke="#f97316"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </Modal>
  );
};

export default SleepData;
